import Link from 'next/link';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { isItUndefined, isLinkAbsolute } from '../../util/DataUtil';
import { getBackendUrl } from '../../util/Constants';
import { cleanHTML } from '../utils/htmlUtils';

library.add(fal);

const HEADING_REGEX = /<h[1-6][\s>]/i;
// Drupal builds file URLs from the internal cluster address on QA/UAT/prod
const INTERNAL_DRUPAL_ORIGIN = /^https?:\/\/drupal(-app)?-service:8080/;

// Public Drupal origin for the current environment, e.g. https://dlr-backend.getdeveloper.net
const getPublicDrupalOrigin = (host) => {
  const remoteServer = getBackendUrl(host)?.remoteServer;
  if (!remoteServer) return '';
  try {
    return new URL(remoteServer).origin;
  } catch {
    return '';
  }
};

// The file wins over the link when both are set
const getDownloadUrl = (item, publicOrigin) => {
  const file = item?.field_download_item_file?.[0];
  const fileUrl = file?.value || file?.url;
  if (fileUrl) {
    const href = publicOrigin
      ? fileUrl.replace(INTERNAL_DRUPAL_ORIGIN, publicOrigin)
      : fileUrl;
    return { href, isFile: true };
  }

  const link = isItUndefined(item?.field_download_item_link)?.trim();
  if (link) return { href: link, isFile: false };

  return null;
};

const DownloadsBoxTitle = ({ html }) => {
  if (!html) return null;

  // Editor picked a heading in the text format, so render it as is
  if (HEADING_REGEX.test(html)) {
    return (
      <div
        className="downloads-box-title"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  // No heading picked, default to h4
  return (
    <h4
      className="downloads-box-title"
      dangerouslySetInnerHTML={{
        __html: html.replace(/<\/?p[^>]*>/gi, '').trim(),
      }}
    />
  );
};

const DownloadsBox = ({ data }) => {
  const publicOrigin = getPublicDrupalOrigin(
    useSelector((state) => state.host.host)
  );
  // Drop empty paragraphs the editor leaves around the heading
  const titleHtml = cleanHTML(isItUndefined(data?.field_downloads_box_title))
    .replace(/<p[^>]*>\s*<\/p>/gi, '')
    .trim();
  const items = (data?.field_downloads_box_items?.download_item || [])
    .map((item) => ({
      title: isItUndefined(item?.field_download_item_title),
      download: getDownloadUrl(item, publicOrigin),
    }))
    .filter((item) => item.title && item.download);

  if (!titleHtml && !items.length) return null;

  return (
    <div className="downloads-box-container inner-container">
      <div className="downloads-box">
        <DownloadsBoxTitle html={titleHtml} />
        {items.length > 0 && (
          <ul className="downloads-box-items">
            {items.map(({ title, download }, index) => {
              // Same as template-download: files and external links open in a new tab
              const openInNewTab =
                download.isFile || isLinkAbsolute(download.href);

              return (
                <li
                  key={`downloads-box-item-${index}`}
                  className="downloads-box-item"
                >
                  <Link href={download.href} passHref legacyBehavior>
                    <a
                      download={download.isFile ? '' : undefined}
                      target={openInNewTab ? '_blank' : '_self'}
                      rel={openInNewTab ? 'noopener noreferrer' : undefined}
                    >
                      <FontAwesomeIcon
                        className="downloads-box-icon"
                        icon={['fal', 'arrow-down-to-bracket']}
                      />
                      <span>{title}</span>
                    </a>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DownloadsBox;
